import { motion, AnimatePresence } from 'framer-motion';
import { TransferProgress } from './TransferProgress';
import type { PeerData, FileTransfer } from '../types';
import { validateFileSize } from '../utils/fileUtils';
import type { UserTier } from '../types';

interface FileTransferPanelProps {
  isOpen: boolean;
  selectedPeer: PeerData | null;
  selectedFile: File | null;
  transfers: FileTransfer[];
  userTier: UserTier;
  onSendFile: () => void;
  onClose: () => void;
  onUpgrade?: () => void;
}

export function FileTransferPanel({
  isOpen,
  selectedPeer,
  selectedFile,
  transfers,
  userTier,
  onSendFile,
  onClose,
  onUpgrade,
}: FileTransferPanelProps) {
  console.log('FileTransferPanel render:', { isOpen, selectedPeer: selectedPeer?.name, selectedFile: selectedFile?.name });
  
  // Check file size validation
  const validationError = selectedFile ? validateFileSize(selectedFile.size, userTier) : null;
  
  return (
    <AnimatePresence mode="wait">
      {isOpen && selectedPeer && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]"
            onClick={onClose}
          />

          {/* Panel */}
          <div
            className="fixed bottom-0 left-0 right-0 z-[70] md:bottom-8 md:left-1/2 md:-translate-x-1/2 md:max-w-2xl md:rounded-2xl overflow-hidden"
          >
            <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-white/10 shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    {selectedPeer.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      Send to {selectedPeer.name}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {selectedPeer.connected ? 'Connected' : 'Connecting...'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Loading state when no file yet */}
                {!selectedFile && transfers.length === 0 && (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading file...</p>
                  </div>
                )}

                {/* File Size Error */}
                {selectedFile && validationError && transfers.length === 0 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center"
                  >
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                      <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    
                    <h4 className="text-xl font-semibold text-white mb-2">File Too Large</h4>
                    <p className="text-gray-400 mb-6">{validationError}</p>
                    
                    <div className="bg-white/5 rounded-xl p-4 mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                          <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-sm font-medium text-white truncate">{selectedFile.name}</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Upgrade CTA */}
                    <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                          </svg>
                        </div>
                        <div className="text-left flex-1">
                          <h5 className="font-semibold text-white mb-1">Upgrade to Premium</h5>
                          <p className="text-sm text-gray-300 mb-2">
                            Get unlimited file transfers for just <span className="font-bold text-blue-400">$1</span> — lifetime access!
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={onClose}
                        className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium rounded-lg transition-all"
                      >
                        Cancel
                      </button>
                      {onUpgrade && (
                        <button
                          onClick={onUpgrade}
                          className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-lg transition-all shadow-lg hover:shadow-xl"
                        >
                          Upgrade Now
                        </button>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Confirmation Message */}
                {selectedFile && !validationError && transfers.length === 0 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center"
                  >
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <svg className="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    
                    <h4 className="text-xl font-semibold text-white mb-2">Send File?</h4>
                    <p className="text-gray-400 mb-6">Confirm you want to send this file to {selectedPeer.name}</p>
                    
                    <div className="bg-white/5 rounded-xl p-4 mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                          <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-sm font-medium text-white truncate">{selectedFile.name}</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={onClose}
                        className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium rounded-lg transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={onSendFile}
                        disabled={!selectedPeer.connected}
                        className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all shadow-lg hover:shadow-xl disabled:shadow-none"
                      >
                        {selectedPeer.connected ? 'Send' : 'Connecting...'}
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Active Transfers */}
                {transfers.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-3"
                  >
                    <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">
                      Transfer in Progress
                    </h4>
                    {transfers.map((transfer) => (
                      <TransferProgress key={transfer.id} transfer={transfer} />
                    ))}
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
